const CLOUDINARY_HOST_PATTERN = /res\s*\.\s*cloudinary\s*\.\s*com/i;
const CLOUDINARY_SCHEME_PATTERN = /^cloudinary:\/\//i;
const HTTP_SCHEME_PATTERN = /^https?\s*:\s*\/\s*\//i;
const PROTOCOL_RELATIVE_PATTERN = /^\/\s*\//;

function normalizeHttpScheme(value) {
  if (HTTP_SCHEME_PATTERN.test(value)) {
    return value.replace(HTTP_SCHEME_PATTERN, (match) =>
      match.toLowerCase().startsWith("https") ? "https://" : "http://"
    );
  }

  if (PROTOCOL_RELATIVE_PATTERN.test(value)) {
    return `https://${value.replace(PROTOCOL_RELATIVE_PATTERN, "")}`;
  }

  return value;
}

function normalizeCloudinaryUrl(value) {
  const compact = normalizeHttpScheme(value).replace(/\s+/g, "");
  return compact.startsWith("http") ? compact : `https://${compact.replace(/^\/+/, "")}`;
}

function normalizeUrlStructure(value) {
  return normalizeHttpScheme(value)
    .replace(/\s*([./?=&:#])\s*/g, "$1")
    .replace(/\s+/g, " ");
}

function normalizeCloudinaryScheme(value) {
  const publicId = value.replace(CLOUDINARY_SCHEME_PATTERN, "").replace(/^\/+/, "");
  if (!publicId) return "";

  return `https://res.cloudinary.com/dspivvtjd/image/upload/${publicId}`;
}

export function normalizeImageUrl(value) {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return "";

  if (CLOUDINARY_SCHEME_PATTERN.test(trimmed)) {
    return normalizeCloudinaryScheme(trimmed);
  }

  if (CLOUDINARY_HOST_PATTERN.test(trimmed)) {
    return normalizeCloudinaryUrl(trimmed);
  }

  const normalized = normalizeUrlStructure(trimmed);

  if (/^https?:\/\//i.test(normalized)) {
    try {
      return encodeURI(decodeURI(normalized));
    } catch {
      return encodeURI(normalized);
    }
  }

  return normalized;
}

export function getOptimizedImageUrl(value, options = {}) {
  const normalized = normalizeImageUrl(value);
  if (!normalized || !normalized.includes("/image/upload/")) return normalized;

  const {
    width,
    quality = "auto:eco",
    format = "auto",
    crop = "limit",
  } = options;
  const transformations = [
    format ? `f_${format}` : "",
    quality ? `q_${quality}` : "",
    crop ? `c_${crop}` : "",
    width ? `w_${Math.round(width)}` : "",
  ].filter(Boolean);

  return normalized.replace(
    "/image/upload/",
    `/image/upload/${transformations.join(",")}/`
  );
}

export function getResponsiveImageSrcSet(value, widths, options = {}) {
  const normalized = normalizeImageUrl(value);
  if (!normalized?.includes("/image/upload/")) return undefined;

  return widths
    .map((width) => `${getOptimizedImageUrl(normalized, { ...options, width })} ${width}w`)
    .join(", ");
}

export function normalizeProductImages(product) {
  if (!product || typeof product !== "object") return product;

  const gallery = Array.isArray(product.gallery)
    ? product.gallery.map(normalizeImageUrl).filter(Boolean)
    : [];
  const image = normalizeImageUrl(product.image) || gallery[0] || "";
  const normalizedGallery = gallery.length
    ? [...new Set(image ? [image, ...gallery] : gallery)]
    : image
      ? [image]
      : [];

  return {
    ...product,
    image,
    gallery: normalizedGallery,
    details: product.details && typeof product.details === "object"
      ? {
          ...product.details,
          image: normalizeImageUrl(product.details.image),
        }
      : product.details,
  };
}
